import { Suspense, useEffect } from "react";
import { Link, Outlet, useLocation, useMatch, useNavigate } from "react-router";
import { t, type StringKey } from "@wr/i18n";
import {
  Avatar,
  BrandLockup,
  ChromeNickname,
  CoinChip,
  ConnectionBanner,
  Mascot,
  TopBar,
  WordLoader,
  useScrolled,
} from "@wr/ui";
import { GuestLogoutDialog } from "../components/GuestLogoutDialog";
import { NavDrawerContent } from "../components/NavDrawerContent";
import { ProfileFlyoutContent } from "../components/ProfileFlyoutContent";
import { useShopEnabled } from "../hooks/useShopEnabled";
import { bannerText } from "./banner-copy";
import { useBalance } from "./BalanceProvider";
import { useBalanceDim } from "./useBalanceDim";
import { useLogout } from "./useLogout";
import { useOverlay } from "./OverlayProvider";
import { useScreenTransport } from "./TransportProvider";
import { useSession } from "./SessionProvider";
import { useNetworkStatus } from "./useNetworkStatus";
import { useRequestLogout } from "./useRequestLogout";

/**
 * Top bar + connection banner + the routed screen + the overlay set.
 *
 * **Which screens have chrome is LOCKED and the list is exhaustive** (§10):
 * the splash, every auth screen, `/welcome` and the in-game screen have none.
 * That is expressed structurally — those routes are simply not children of this
 * element — rather than as a `hideChrome` prop somebody can forget.
 *
 * §10 amendment 1: the centre slot is the `BrandLockup` on the primary screen
 * (Lobby) and the screen *title* everywhere else, with the mascot dropped.
 *
 * **AMENDED by Ivy lobby §23.3 (S11).** That amendment used to end *"…
 * non-interactive either way (PRD §5.1) — no link, no tab stop"*, and the half
 * that changed is the second one, on non-lobby screens only:
 *
 *  - **on the lobby**, still the inert `BrandLockup` — a link to the page you
 *    are on is a dead control;
 *  - **everywhere else**, the centre also carries a **home link to `/lobby`**:
 *    a 24 px mascot before the title below 1024, and the lockup itself at
 *    ≥1024, where it returns to the left cluster. One authored accessible name
 *    (`nav.home.a11y`) on both, no `aria-describedby` sibling.
 *
 * PRD §5.1's real content survives: the *component* is still non-interactive
 * and still renders no control of its own. What is interactive is the `<a>`
 * around it — never a `<button>` with an `onClick`, because it changes
 * location.
 */

const SCREEN_TITLES: Record<string, StringKey> = {
  "/settings": "settings.title",
  "/profile": "profile.title",
  // S8 E7 (T-404), Ivy §4.2: "Top bar present (centre slot = the screen
  // title)" on every `/shop` state, flag on or off.
  "/shop": "shop.title",
};

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrolled = useScrolled();
  /**
   * **T-440 / SHOP-ENTRY-POINTS surface 2.** One home for all four entry
   * points (Ivy storefront ruling addendum-d §1, LOCKED) — the drawer row
   * below reads the same hook, so the two chrome surfaces cannot disagree.
   */
  const shopEnabled = useShopEnabled();
  const { profile, offline, reload } = useSession();
  const { overlay, toggle, close } = useOverlay();
  const screen = useScreenTransport();
  const { balanceCoins, witnessed } = useBalance();
  const transport = useNetworkStatus(offline, screen);
  /**
   * **T-260 — extracted, not changed.** This was the inline expression
   * `screen.stale || transport === "browser-offline" || reconcileDim`, and it
   * still computes exactly that (`transport === "browser-offline"` is precisely
   * `!navigator.onLine`, since the device wins the merge's precedence). It moved
   * into a hook because Ivy's profile §9.2 makes the same predicate govern the
   * profile screen's balance plate, and one wallet rendered on two surfaces must
   * dim on one fact, not on two copies of it.
   */
  const balanceDim = useBalanceDim();

  /**
   * **The lobby is the one screen where the brand mark stays inert** (Ivy
   * §23.3) — *"a link to the page you are on is a dead control"*.
   *
   * **T-502 F-0.** This was `location.pathname === "/lobby"`, an exact string
   * compare, and the router does not resolve paths that way: `matchPath`
   * compiles `/lobby` to `/^\/lobby\/*$/i`, so `/lobby/` and `/LOBBY` are the
   * **same route** and both used to paint a home link on the lobby pointing at
   * the lobby. `useMatch` asks the router the question the branch is actually
   * about — *did this location resolve to the lobby route* — instead of
   * re-implementing its answer, so trailing slashes and case cannot drift
   * apart from it again.
   *
   * `/lobby?x=1` was already correct and stays correct: a query string is not
   * part of `location.pathname`, so it matched before and matches now. It is in
   * the test for exactly that reason — it is the case a "normalise the string"
   * fix could easily have broken.
   *
   * **Called here, above `if (!profile) return null`, deliberately.** It is a
   * hook, and as a `useMatch` below that early return it would be a
   * conditionally-called one — the same trap `useRequestLogout` was moved out
   * of, which React tolerated only because the early return happens on a
   * session's first render and never afterwards.
   */
  const lobbyMatch = useMatch("/lobby");

  /**
   * §14.1 ruling C: a log-out is a departure the player chose, so it lands on a
   * bare `/login` and drops any `?next=` the screen was carrying. The hook owns
   * that rule for all three call sites (here, `/welcome`, `/verify`).
   */
  const logout = useLogout();

  /**
   * §11 LOCKED: a guest is warned, a registered player is not — the drawer and
   * the flyout both log out and must behave identically, and from T-260 the
   * profile screen's Account row is a third trigger on the same predicate.
   *
   * **Extracted rather than forked** (Ivy profile §7.1 / §15 rule 9). It also
   * moved *above* the `if (!profile) return null` below: as a `useCallback`
   * underneath an early return it was a conditionally-called hook, which React
   * tolerated only because the early return happens on the first render of a
   * session and never afterwards.
   */
  const requestLogout = useRequestLogout();

  /**
   * §4.5 seam 1 — **the drawer and the profile flyout close automatically** when
   * the round takes the screen. Their owner (the top bar) is gone and every row
   * in them is behind S4 §3.1's navigation guard anyway; leaving one open would
   * put a scrim over a live board, which §5.13 LOCKS against.
   */
  const immersive = screen.immersive;
  useEffect(() => {
    if (immersive) close();
  }, [close, immersive]);

  if (!profile) return null;

  /**
   * **The same exact-compare mechanism, on a different branch — recorded, not
   * fixed here (T-503 is scoped to `atLobby`).** `/SETTINGS` resolves to the
   * settings route and gets no title, because this is a keyed lookup on the
   * raw pathname. The consequence is benign and is not the F-0 defect: with no
   * title the shell takes the *title-less* branch, which still renders a home
   * link (the lockup one) and still never renders a self-link. Filed in the
   * T-503 Output as the sweep clause for this mechanism.
   */
  const titleKey = SCREEN_TITLES[location.pathname];
  /**
   * Inertness is a fact about the *destination* — you are already there — not
   * about the layout, which is why this is a route test and not a
   * screen-reported flag. See `lobbyMatch` above for why it is a match rather
   * than a string compare.
   */
  const atLobby = lobbyMatch !== null;

  /**
   * **§2.2, LOCKED — the round takes all three bands, and this is the shell's
   * half of it.** The mounted screen has reported that it owns the whole
   * viewport, so the top bar and the banner do not render: the wrapper renders
   * the banner itself, pinned under its own chrome as an overlay, because an
   * in-flow banner during a round reflows the grid mid-drag (§4.6).
   *
   * Expressed as *"the screen said so"* rather than *"the route is `/room`"*: the
   * room screen is immersive for one of its eleven states and ordinary for the
   * other ten, and a path test would take the chrome away from all of them.
   *
   * **It is a conditional inside ONE tree, deliberately, not an early return.**
   * An early return puts `<Outlet/>` at a different position in a different
   * fragment, so React unmounts and remounts the routed screen at the phase
   * boundary — mid-round, on the one screen the product LOCKS as *"one route, one
   * mount, for the entire lifecycle"* (§2.1, S4 §2). Everything the screen holds
   * in local state (the leave dialog, the cold-entry guard, a toast) would be
   * thrown away at exactly the moment a player has money on the table. The
   * `<main>` element stays in place and only its class changes.
   *
   * **Quinn: no coin chip, no hamburger and no avatar during an active round.
   * That is correct, not a missing element.**
   */
  return (
    <>
      {immersive ? null : (
        <TopBar
          menuOpen={overlay === "drawer"}
          menuLabel={overlay === "drawer" ? t("nav.close") : t("nav.menu")}
          onMenu={() => toggle("drawer")}
          scrolled={scrolled}
          centre={
            <>
              {/**
               * **The way home — Ivy lobby §23.3, which AMENDS §10 amendment 1.**
               *
               * Aaron: *"The logo at the top should be clickable and take you to
               * the lobby if on a different page."* Two things had to move for
               * that: on a non-lobby screen there was **no logo to click** (the
               * centre slot is the screen title below 1024, and at ≥1024 the
               * title is `display: none` and nothing replaced it), and the
               * lockup was LOCKED non-interactive.
               *
               * **On the lobby it stays inert** — a link to the page you are on
               * is a dead control — so `atLobby` is the whole branch, and it is
               * a path test rather than "does this screen have a title" because
               * *inertness is about the destination*, not about the layout.
               *
               * **`<Link>`, never a `<button>` with an `onClick`.** It changes
               * location, so middle-click, ⌘-click, "open in new tab" and the
               * status-bar preview all have to work; a button that navigates is
               * a link wearing a costume. That is also why `go()`-style
               * `navigate()` is *not* used here even though the drawer uses it.
               *
               * **Never a heading, never `data-route-heading`** (§20.2,
               * §22.20/§22.21): route-change focus belongs in the content.
               */}
              {atLobby || !titleKey ? null : (
                <Link
                  to="/lobby"
                  className="wr-topbar__home wr-topbar__home--mascot"
                  aria-label={t("nav.home.a11y")}
                  data-testid="topbar-home-mascot"
                >
                  {/* Already `aria-hidden` inside `Mascot`; the link owns the name. */}
                  <Mascot size={24} hero={false} />
                </Link>
              )}
              {titleKey ? (
                /**
                 * **A `<p>`, not an `<h1>` — S2 §20.2, LOCKED (T-183 finding 3).**
                 *
                 * `/settings` and `/profile` rendered two `<h1>`s with the same
                 * accessible name: this one and the panel's own. Ivy's ruling is
                 * that the top-bar title **is not a heading** — it is banner text.
                 * It stays in the accessibility tree (a duplicated *label* inside
                 * `role="banner"` is not a defect, and `aria-hidden` on visible text
                 * is a defect of its own); it takes no `role`; and it must never
                 * carry `data-route-heading`, because route-change focus belongs in
                 * the content, not the chrome.
                 *
                 * The room screen relies on this: §3 gives its centre slot the same
                 * string as its `<h1>`, so a heading here would be the same defect
                 * on the most-visited screen in Sprint 4.
                 */
                <p className="wr-display-sm wr-topbar__title">{t(titleKey)}</p>
              ) : null}
              {atLobby ? (
                <BrandLockup
                  size="sm"
                  wordy={t("app.wordy")}
                  royale={t("app.royale")}
                  appName={t("app.name")}
                />
              ) : (
                /**
                 * **§23.3's ≥1024 row: the lockup returns to the left cluster and
                 * is the home link** — which is also §10's own shipped promise
                 * ("left cluster: hamburger + `BrandLockup`"), unmet since the
                 * title went `display: none` there with nothing in its place. The
                 * centre column is `justify-content: flex-start` at ≥1024, so it
                 * *is* that cluster; no grid change was needed.
                 *
                 * **On a title-less non-lobby screen it is visible at every
                 * width** (`--wide` is dropped), because there the lockup is
                 * already the centre's only content and making it the link is the
                 * entire change. Adding the mascot there too would put two home
                 * links in one viewport, which is the thing §13.2 is actually
                 * about. Today that is `/room` outside a round; §23.3's table does
                 * not enumerate the title-less case and this is the reading that
                 * changes least — recorded in the ticket Output as a judgment call.
                 *
                 * `decorative` so the `<a>` carries the one accessible name and
                 * the lockup's own `role="img"` label does not double it.
                 */
                <Link
                  to="/lobby"
                  className={[
                    "wr-topbar__home",
                    "wr-topbar__home--lockup",
                    titleKey ? "wr-topbar__home--wide" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={t("nav.home.a11y")}
                  data-testid="topbar-home-lockup"
                >
                  <BrandLockup
                    size="sm"
                    wordy={t("app.wordy")}
                    royale={t("app.royale")}
                    appName={t("app.name")}
                    decorative
                  />
                </Link>
              )}
            </>
          }
          right={
            <>
              {/**
               * **Live from S4-8**, and it went live exactly as §9.2's LOCKED
               * progression said it would: one nullable field, one flag. The stub
               * (`—`, `aria-hidden`, not focusable, no `+`) is still what a client
               * with no balance source renders — `null` means "no claim is being
               * made", never zero — and `shop_enabled` is still `false`, so the
               * chip is `live-inert`: a real number that is deliberately not
               * pressable until the Shop exists (S7).
               *
               * **It dims while the feed is stale — S3 §20.4, which REVERSED
               * §19.1's chrome exception** (and room spec §11.2 exception 3, §19
               * line 33). §19.1 wrote "bright and inert" about a chip that could
               * not move under a mounted screen; S4-8 made it a pushed number on
               * the screen's own socket, so during an outage it is exactly as
               * frozen as the room around it and bright would be the appearance of
               * live behaviour with none of it.
               *
               * The signal is **the screen's** (`screen.stale`), which is §20.1's
               * fact 1 and the same fact the banner's action is built on — never a
               * transport state name. A screen with no socket reports nothing and
               * the chip does not dim; that falls out rather than being a case.
               * **Only the chip dims**: the hamburger, the title, the avatar and
               * the banner below stay at full opacity.
               *
               * **`screen.stale` now means a *genuine outage* — S2 §21.2 sharpened
               * lobby §20.4's trigger, and the device half of it is here.** A socket
               * that closed because the room it was attached to ended leaves nothing
               * stale, so a settled result shows a **bright** chip (the balance is
               * current by both routes: warm, the credit landed seconds earlier;
               * cold, the page read it over HTTP). But `navigator.onLine === false`
               * *is* an outage on every screen, socket or no socket — nothing can
               * refresh this number — and it is a **device** fact, so it is merged
               * here rather than re-derived by each screen. That is the same
               * precedence the banner above already runs on ("the device first, then
               * the screen"), and it is what makes §21.2's row 3 true: the network
               * dies over a terminal panel, the banner says `no internet`, and the
               * chip dims with it.
               *
               * `animate` is §9.4's motion gate: a credit that lands while
               * connected still animates, and the **reconciled** value on recovery
               * lands as an instant swap with no cross-fade and no pulse.
               */}
              <CoinChip
                value={balanceCoins}
                label={t("flyout.balance")}
                /**
                 * **The `live` chip's accessible name and description** — Ivy
                 * §9.2 ADDENDUM 2026-08-18 (b), FINAL, Nora-passed.
                 *
                 * A `<button>` is a name-from-content role, so the component's
                 * `aria-label` REPLACES the subtree: with `flyout.balance`
                 * alone the pill announced "Coin balance, button" and the
                 * number was gone. `coin.a11y.balance` carries it.
                 *
                 * **`toLocaleString`, never `formatCoins`.** The visible
                 * numeral abbreviates at 100,000 (`12.4k`) for a pixel budget
                 * the ear does not have; the name says "12,400". This is the
                 * one place the two deliberately disagree, and it is the
                 * reason the interpolation is here rather than in `@wr/ui` —
                 * the component renders `formatCoins` and must not be the
                 * thing that decides they differ.
                 *
                 * `?? 0` is unreachable: the component reads `liveLabel` only
                 * in the `live` state, which `coinChipState` gives only for a
                 * non-null value. It is a type floor, not a fallback value.
                 */
                liveLabel={t("coin.a11y.balance", { n: (balanceCoins ?? 0).toLocaleString() })}
                liveDescription={t("coin.a11y.action")}
                /**
                 * **§9.2's third state, built at last (T-440).** The chip has
                 * carried `live` in `@wr/ui` since S4-8 with nothing to pass
                 * it: *"`live` | number, `shop_enabled === true` | as above
                 * **plus** the `+` affordance inside the pill | **yes** — the
                 * whole pill navigates to Shop; the `+` is decoration inside
                 * that one target"* (S3 §9.2, LOCKED; addendum-d §1 row 2).
                 *
                 * **One target, and it is the pill.** The `+` is `aria-hidden`
                 * decoration inside the component's single `<button>` — S2 §10
                 * amendment 3 bars a nested control here, and this call site
                 * cannot introduce one because the component owns the element.
                 *
                 * `onClick` is passed unconditionally: the component ignores it
                 * in `stub` and `live-inert` (it renders no button at all in
                 * those states), so the flag alone decides whether a player can
                 * press anything. **Dimming is not disabling** (§20.4
                 * guardrail 3) — a stale feed keeps the chip pressable, because
                 * the Shop does its own authoritative read.
                 */
                shopEnabled={shopEnabled}
                onClick={() => void navigate("/shop")}
                /**
                 * **Q10 (§14.3 part 4) — two independent dims, and the chip is
                 * bright only if neither holds.** The *transport* dim says the feed
                 * is not current; the *reconcile* dim says the number has not been
                 * verified since a settlement. §21.2's "expected close ⇒ bright
                 * chip" is narrowed to "expected close ⇒ no **transport** dim" — it
                 * never licensed brightness on an unverified number.
                 */
                dimmed={balanceDim}
                animate={witnessed}
              />
              <button
                type="button"
                className="wr-topbar__avatar-btn wr-topbar__avatar-btn--mobile"
                aria-label={t("flyout.open")}
                aria-expanded={overlay === "flyout"}
                data-testid="topbar-avatar"
                onClick={() => toggle("flyout")}
              >
                <Avatar
                  playerId={profile.id}
                  nickname={profile.nickname}
                  url={profile.avatarUrl}
                  size={34}
                />
              </button>
              {/* Desktop's profile pill — the same control, a wider arrangement. */}
              <button
                type="button"
                className="wr-profile-pill"
                /**
                 * **The pill's own name carries the full nickname — S14 `A5`,
                 * Ivy R-K3's failing branch.**
                 *
                 * This button used to read `t("flyout.open")` (*"Your
                 * profile"*), which **overrides its whole subtree**: the inner
                 * nickname span's `aria-label` could never be spoken, so §14.1
                 * ruling B item 3 (*"the full nickname is never lost"*) was
                 * **UNMET on this surface** — not merely carried redundantly.
                 * R-K3 says that where the check fails the fix moves to the
                 * button's label, and R-K4 makes the promise a surface-level
                 * one satisfied **once**. This is that once.
                 *
                 * **The mobile avatar button above keeps `flyout.open`** and
                 * that asymmetry is deliberate: it renders no nickname, so
                 * ruling B item 3 does not bind it.
                 */
                aria-label={t("flyout.openNamed", { nickname: profile.nickname })}
                aria-expanded={overlay === "flyout"}
                data-testid="topbar-profile-pill"
                onClick={() => toggle("flyout")}
              >
                <Avatar
                  playerId={profile.id}
                  nickname={profile.nickname}
                  url={profile.avatarUrl}
                  size={28}
                />
                {/*
                  §14.1 ruling B item 4 ("anywhere a nickname renders in fixed-
                  width chrome"). The pill's label is `label-md`, below the
                  display ramp the size step operates on, so what applies here
                  is the one-line half of the rule: ellipsis, plus the full name
                  in `title` (R-K2) and — under R-K4 — in the button's own
                  accessible name above rather than a second time in here.

                  **This was a hand-inlined copy of `ChromeNickname` and that
                  was the defect** (Ivy: *"A1's fix in `@wr/ui` did not reach
                  this site, and the next fix will not either"*; spec pin 2's
                  *"a second copy is a defect, not an optimisation"*). It is now
                  the shared component, at the same `label-md` rung, so the
                  rendering is byte-identical and the next chrome-nickname
                  ruling reaches all three surfaces at once.
                */}
                <ChromeNickname
                  nickname={profile.nickname}
                  base="label-md"
                  nameExposure="ancestor"
                />
              </button>
            </>
          }
        />
      )}

      {/**
       * §13.2 part 2: these screens have no in-content recovery control, so the
       * banner carries `Retry`. Four cases, in order:
       *
       *  - `browser-offline` and `restored` get **none** — there is nothing to
       *    retry when the device has no network at all, and nothing to recover
       *    once it is back.
       *  - A screen that reported its own transport supplies its own recovery
       *    (the lobby's rejoin). §11.3 rows 3 and 4 put `Retry` on `reconnecting`
       *    as well as on `offline` for that screen, which is why this is not
       *    gated on `offline` alone any more: a frozen lobby with no button was
       *    T-191 B2.
       *  - **A reporting screen that explicitly passes no retry gets none —
       *    the shell never substitutes an action for a control the screen
       *    declined (§21.1, T-221; Quinn T-220).** `RoomScreen` reports
       *    `retry: null` on a settled `results`/terminal because
       *    `freezesContent` says a reconnect restores nothing there; the old
       *    `screen.retry ?? (() => void reload())` treated that `null` the
       *    same as "nobody is reporting" and re-manufactured a button pointed
       *    at the session reload, which cannot revive a disposed room. The
       *    distinguishing fact is `screen.reporting`: `true` means an active
       *    screen made a deliberate choice about its own recovery, so `null`
       *    there is final. `false` (`TransportProvider`'s `IDLE`) means no
       *    screen is reporting at all, which is the legacy-screen case below.
       *  - Otherwise (no screen is reporting) the failure is the session's,
       *    and reloading it is the remedy.
       */}
      {immersive ? null : (
        <ConnectionBanner
          state={transport}
          message={bannerText(transport)}
          action={
            (transport === "reconnecting" || transport === "offline") &&
            !(screen.reporting && screen.retry === null)
              ? {
                  label: t("net.retry"),
                  onAction: screen.retry ?? (() => void reload()),
                }
              : undefined
          }
        />
      )}

      <main
        className={immersive ? "wr-app-immersive" : "wr-app-column"}
        style={immersive ? undefined : { padding: "24px var(--wr-gutter) 48px" }}
      >
        {/*
          T-373 P1: the authenticated screens (`/lobby`, `/room`, `/profile`,
          `/settings`) are `React.lazy` in `App.tsx`, so their chunk resolves when
          the route is first reached. The boundary is HERE, inside the shell, so
          the top bar / coin chip / banner stay painted and only the screen area
          shows the loader — the chrome does not blank on a route swap. The
          pre-login screens are eager and never suspend, so first paint carries no
          fallback. `WordLoader` is the app's existing indeterminate loader; the
          label is an aria-label only (the visual is animated cells), reusing the
          `splash.loading` string rather than introducing new copy.
        */}
        <Suspense
          fallback={
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <WordLoader label={t("splash.loading")} />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      {/**
       * §4.5 seam 1: *"the drawer or the profile flyout is open → **both close
       * automatically**. Their owner has unmounted and their rows are barred by
       * the guard."* Not rendering them is the same statement as closing them, and
       * it is the one that cannot be forgotten — the effect above closes the state
       * so nothing re-opens behind the round.
       */}
      {immersive ? null : <NavDrawerContent profile={profile} onLogout={requestLogout} />}
      {/*
        `balanceCoins` is passed, not re-read: the flyout's plate and the chip
        above it are two renderings of one wallet, and the chip's number is the
        socket's when the socket is ahead (T-217, Quinn B1's sibling).
      */}
      {immersive ? null : (
        <ProfileFlyoutContent
          profile={profile}
          balanceCoins={balanceCoins}
          onLogout={requestLogout}
          offline={offline}
        />
      )}
      <GuestLogoutDialog onConfirm={() => void logout()} />
    </>
  );
}
